import { CheckOutlined } from '@ant-design/icons';
import { Form, Typography } from 'antd';
import _, { uniqBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { CustomButton } from '@/common/Buttons/CustomButton';
import CustomSelect from '@/common/CustomSelect';
import SpinnerLoader from '@/common/Loader/SpinnerLoader/index';
import { TextAreaComponent } from '@/common/TextComponent/TextAreaComponent';
import { HeaderRowComponent } from '@/components/Dashboard/CustomModal/ModalMessageComponent';
import { FormItemStyled, ModalMessageStyled } from '@/components/Dashboard/CustomModal/styled';
import { IClient } from '@/interfaces/InterfaceData';
import { sendMessage } from '@/services/messages';
import { openErrorModal, openSuccessModal, openWarningModal } from '../../../common/AlertModal/AlertModal';
import { GLOBAL_CONFIGS } from '../../../configs/configs';
import CONSTANTS from '../../../constant';
import { formatUserDataForSend } from '../../../utils/arrayOrObjectTools';
import localStorageTools from '../../../utils/localStorageTools';

interface ICustomModalProps {
    activeTabs?: string;
    isMinimized?: boolean;
    listClients: any;
    oneOrMoreSelectedUser?: any;
    handleShowModal: () => void;
    handleMinimizedModal: () => void;
    handleMaximizedModal: () => void;
    setSelectedOneOrMoreRow: React.Dispatch<React.SetStateAction<IClient[]>>;
    setSelectedRowKeys: React.Dispatch<React.SetStateAction<React.Key[]>>;
}

const normalizePhoneNumber = (phoneNumber?: string) => `${phoneNumber || ''}`.replace(/\s+/g, '');

const hasAllowedPrefix = (phoneNumber?: string) => {
    const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);

    return normalizedPhoneNumber.startsWith('034') || normalizedPhoneNumber.startsWith('038');
};

const ModalMessageComponent: React.FC<ICustomModalProps> = (props: ICustomModalProps) => {
    const {
        handleShowModal,
        handleMinimizedModal,
        handleMaximizedModal,
        listClients,
        isMinimized,
        activeTabs,
        oneOrMoreSelectedUser,
        setSelectedOneOrMoreRow,
        setSelectedRowKeys,
    } = props;

    const [loading, setLoading] = useState(false);
    const [messageData, setMessageData] = useState<string>('');
    const [isError, setIsError] = useState<boolean>(false);
    const [isSendForAll, setIsSendForAll] = useState<boolean>(false);
    const [invalidPhoneNumbers, setInvalidPhoneNumbers] = useState<string[]>([]);
    const { Text } = Typography;

    const buildManualRecipient = (phoneNumber: string) => ({
        phoneNumber,
        firstName: '',
        lastName: '',
        cin: '',
        clientId: null,
    });

    useEffect(() => {
        const allowedClients = (listClients || []).filter((client: IClient) => hasAllowedPrefix(client.phoneNumber));

        listClients && setIsSendForAll(oneOrMoreSelectedUser?.length === allowedClients.length);
    }, [oneOrMoreSelectedUser, listClients]);

    const onChangeTextArea = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.target) setMessageData(e.target.value);
        localStorageTools.saveValueToLocalStorage(GLOBAL_CONFIGS.MESSAGE, e.target.value);
    };

    // Callback to get data from select component
    const getUsersFromSelect = (params: any[]) => {
        const selectedPhoneNumbers = uniqBy(
            (params || [])
                .map((item: any) => normalizePhoneNumber(item?.value))
                .filter((phoneNumber: string) => !_.isEmpty(phoneNumber))
                .map((phoneNumber: string) => ({ phoneNumber })),
            'phoneNumber',
        );

        const validPhoneNumbers = selectedPhoneNumbers.filter(({ phoneNumber }: { phoneNumber: string }) => hasAllowedPrefix(phoneNumber));
        const rejectedPhoneNumbers = selectedPhoneNumbers
            .filter(({ phoneNumber }: { phoneNumber: string }) => !hasAllowedPrefix(phoneNumber))
            .map(({ phoneNumber }: { phoneNumber: string }) => phoneNumber);

        const recipients = validPhoneNumbers.map(({ phoneNumber }: { phoneNumber: string }) => {
            const matchedClient = listClients?.find((client: any) => normalizePhoneNumber(client.phoneNumber) === phoneNumber);

            return matchedClient || buildManualRecipient(phoneNumber);
        });

        const selectedRowKeys = recipients.filter((client: any) => client?.id).map((client: any) => client.id);

        // Set data by parent callbacks
        setSelectedOneOrMoreRow(recipients);
        setSelectedRowKeys(selectedRowKeys);
        setInvalidPhoneNumbers(rejectedPhoneNumbers);
        setIsError(false);
    };

    // Send data to api for sending SMS
    const messageServicePost = () => {
        let isErrorSms = false;
        const oneOrMoreSelectedUserFormatted = formatUserDataForSend(oneOrMoreSelectedUser);

        sendMessage(null, uniqBy(oneOrMoreSelectedUserFormatted, 'contact'), messageData).then((response: any) => {
            setLoading(false);
            if (response.status === 500) {
                isErrorSms = true;
            }
        });

        // Delete localstorage draft
        localStorageTools.deleteValueFromLocalStorage(GLOBAL_CONFIGS.MESSAGE);

        isErrorSms &&
            openErrorModal({
                onOk: messageServicePost,
            });

        !isErrorSms &&
            openSuccessModal({
                onOk: handleShowModal,
            });
    };

    // Function submit Form in case failed
    const onFinishFailed = () => {
        setIsError(_.isEmpty(oneOrMoreSelectedUser));
    };

    // Check error select
    const onFinish = () => {
        if (!_.isEmpty(invalidPhoneNumbers)) {
            return;
        }

        !_.isEmpty(oneOrMoreSelectedUser)
            ? openWarningModal({
                  onOk: () => messageServicePost(),
              })
            : onFinishFailed();
    };

    if (loading) {
        return <SpinnerLoader />;
    }

    const handleClickAllSelection = () => {
        const allowedClients = (listClients || []).filter((item: IClient) => hasAllowedPrefix(item.phoneNumber));

        setIsSendForAll(!isSendForAll);
        setSelectedOneOrMoreRow(!isSendForAll ? allowedClients : []);
        setSelectedRowKeys(!isSendForAll ? allowedClients.map((item: IClient) => item.id) : []);
        setInvalidPhoneNumbers([]);
    };

    // Check Message Draft as default value in Textarea
    const checkDefaultValue = () => localStorageTools.getValueFromLocalStorage(GLOBAL_CONFIGS.MESSAGE) || '';

    // Hide the button in Sendbox when userSelected <= 1

    const checkDisableButton = (oneOrMoreSelectedUser?.length > 1 && activeTabs === CONSTANTS.tabs.SEND_BOX) || activeTabs === CONSTANTS.tabs.OUT_BOX;

    return (
        <ModalMessageStyled name="form-modal-message" onFinish={onFinish} onFinishFailed={onFinishFailed} autoComplete="off">
            <Form.Item>
                <HeaderRowComponent
                    isMinimized={isMinimized}
                    handleMinimizedModal={() => handleMinimizedModal()}
                    handleMaximizedModal={() => handleMaximizedModal()}
                    handleShowModal={() => handleShowModal()}
                />
            </Form.Item>
            {!isMinimized && (
                <>
                    {checkDisableButton && (
                        <Form.Item>
                            <CustomButton
                                child={
                                    <>
                                        <CheckOutlined />
                                        {!isSendForAll ? CONSTANTS.selectAll : CONSTANTS.unselectAll}
                                    </>
                                }
                                actions={handleClickAllSelection}
                            />
                        </Form.Item>
                    )}
                    <Form.Item>
                        <CustomSelect
                            listClients={uniqBy(listClients, 'phoneNumber')}
                            Selectvalue={uniqBy(oneOrMoreSelectedUser, 'phoneNumber')?.map((client: any) => {
                                const phoneNumber = normalizePhoneNumber(client.phoneNumber);
                                const fullName = `${client.firstName || ''} ${client.lastName || ''}`.trim();

                                return {
                                    label: fullName ? `${fullName} - ${phoneNumber}` : phoneNumber,
                                    value: phoneNumber,
                                };
                            })}
                            getUsersFromSelect={(data: any[]) => getUsersFromSelect(data)}
                        />
                        {isError && _.isEmpty(oneOrMoreSelectedUser) && <Text type="danger"> {CONSTANTS.addTextDestinataire} </Text>}
                        {!_.isEmpty(invalidPhoneNumbers) && <Text type="danger"> {CONSTANTS.invalidPhoneNumberPrefix} </Text>}
                    </Form.Item>
                    <Form.Item name="message" rules={[{ required: true, message: CONSTANTS.addNewMessage }]}>
                        <TextAreaComponent onChange={(e) => onChangeTextArea(e)} defaultValue={checkDefaultValue()} />
                    </Form.Item>
                    <FormItemStyled>
                        <CustomButton htmlType="submit" child={CONSTANTS.sendMessage} />
                    </FormItemStyled>
                </>
            )}
        </ModalMessageStyled>
    );
};

export default ModalMessageComponent;
